/**
 * PLT-7846 — the "Ask Upright to review this" card's data: whether the card was already answered,
 * read from `GET /api/chat/review-requests/...` so a reopened conversation shows it closed, and
 * the user's answer, which replaces that state once the server has it.
 *
 * A card is identified by the assistant message it is in (`messageIndex`), not by its tool call id,
 * which a reopened conversation rebuilds under a new value (`seed-message.ts`).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ReviewRequestAvailabilityResponse,
  ReviewRequestFields,
  ReviewRequestStateResponse,
  ReviewRequestStatus,
} from '@upright/common/chat/review-request-types';
import { fetchOrThrow, fetchOrThrowNoReturnData } from '@upright/ui/api/common/fetchers/fetch-or-throw';

function reviewRequestQueryKey(conversationId: string, messageIndex: number) {
  return ['chat', 'review-request', conversationId, messageIndex] as const;
}

export function getReviewRequestStateQuery(conversationId: string, messageIndex: number) {
  return queryOptions({
    queryKey: reviewRequestQueryKey(conversationId, messageIndex),
    queryFn: async () =>
      await fetchOrThrow<ReviewRequestStateResponse>(
        `/chat/review-requests/${encodeURIComponent(conversationId)}/${messageIndex}`,
      ),
  });
}

/** Whether this user may ask Upright to review a conversation. Fixed for a session, so read once. */
export const reviewRequestAvailabilityQuery = queryOptions({
  queryKey: ['chat', 'review-request', 'availability'] as const,
  queryFn: async () => await fetchOrThrow<ReviewRequestAvailabilityResponse>('/chat/review-requests/availability'),
  staleTime: Infinity,
});

type Answer = { status: 'sent'; fields: ReviewRequestFields } | { status: 'cancelled' };

export function useAnswerReviewRequestMutation(conversationId: string, messageIndex: number) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, Answer>({
    mutationFn: async answer => {
      await fetchOrThrowNoReturnData('/chat/review-requests', 'POST', { conversationId, messageIndex, ...answer });
    },
    onSuccess: (_, answer) => {
      queryClient.setQueryData<ReviewRequestStateResponse>(reviewRequestQueryKey(conversationId, messageIndex), {
        status: answer.status satisfies ReviewRequestStatus,
      });
    },
    // A 409 means the card was answered elsewhere, such as another tab: read what that answer was.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: reviewRequestQueryKey(conversationId, messageIndex) });
    },
  });
}
